import { gatewayClient } from 'utils/gatewayClient';

export interface FetchAccountsResponse {
  accounts: Array<{
    account_detail_info: {
      account_info: Array<{
        additional_deposits: number;
        balance: number;
        employee_relation: string;
        is_dependent: boolean;
        flex_account_key: number;
        first_name: string;
        last_name: string;
        status: string;
      }>;
      account_type: string;
      account_type_class_code: string;
      available_balance: number;
      balance_due: number;
      display_options: number;
      effective_date: string;
      primary_fund_rollover_rule: string;
      remaining_balance: number;
      spending_last_date: string;
      account_end_date?: string;
      account_payroll_info?: {
        contributions_ytd: number;
        employee_annual_election: number;
        employee_ppp_contribution: number;
        employer_ppp_contribution_amount: number;
        employer_annual_election: number;
        payroll_cycle: string;
        plan_start_date?: string;
        plan_end_date?: string;
      };
      submit_claims_last_date?: string;
    };
    account_display_header: string;
    account_status_code: string;
    account_type: string;
    account_type_class_code: string;
    annual_election: number;
    bank_routing_no: string;
    flex_account_id: string;
    grace_period_end_date: string;
    grace_period_end_date_formatted: string;
    hsa_routing_no?: string;
    is_card_eligible: boolean;
    plan_end_date: string;
    plan_end_date_formatted?: string;
    plan_start_date: string;
    plan_start_date_formatted?: string;
    total_hsa_balance?: number;
  }>;
}

export function fetchAccounts() {
  return gatewayClient.get<FetchAccountsResponse>('/cdh/v2/accounts');
}

export interface FetchAccountDetailResponse {
  account_detail_info: {
    account_info: Array<{
      additional_deposits: number;
      balance: number;
      employee_relation: string;
      is_dependent: boolean;
      flex_account_key: number;
      first_name: string;
      last_name: string;
      status: string;
    }>;
    account_type: string;
    account_type_class_code: string;
    available_balance: number;
    balance_due: number;
    display_options: number;
    effective_date: string;
    primary_fund_rollover_rule: string;
    remaining_balance: number;
    spending_last_date: string;
    account_end_date?: string;
    account_payroll_info?: {
      contributions_ytd: number;
      employee_annual_election: number;
      employee_ppp_contribution: number;
      employer_ppp_contribution_amount: number;
      employer_annual_election: number;
      payroll_cycle: string;
      plan_start_date?: string;
      plan_end_date?: string;
    };
    submit_claims_last_date?: string;
  };
}

export function fetchAccountDetail({
  account_id,
  account_type,
}: {
  account_id: string;
  account_type: string;
}) {
  return gatewayClient.get<FetchAccountDetailResponse>(
    `/cdh/v2/accounts/${account_id}/types/${account_type}`
  );
}

export interface FetchHSASummaryResponse {
  summary: {
    current_year: {
      year: number;
      contributions: number;
      total_contributions: number;
      family_limit: number;
      single_limit: number;
    };
    year_to_date: {
      employee_contributions: number;
      employer_contributions: number;
      total_contributions: number;
    };
    prior_year: {
      employee_contributions: number;
      employer_contributions: number;
      total_contributions: number;
      family_limit: number;
      single_limit: number;
    };
    total_available_to_invest: number;
    total_available_to_spend: number;
    total_hsa_balance: number;
    current_hsa_balance: number;
    trust_account: {
      number: string;
      min_balance: number;
    };
    hsa_coverage_tier: string;
    hsa_account_number: string;
  };
}

export function fetchHSASummary({ account_id }: { account_id: string }) {
  return gatewayClient.get<FetchHSASummaryResponse>(
    `/cdh/v2/accounts/${account_id}/hsa-summary`
  );
}

export interface FetchHSAInvestmentsResponse {
  investments: {
    portfolio_balance: number;
    total_balance: number;
  };
}

export function fetchHSAInvestments({ account_id }: { account_id: string }) {
  return gatewayClient.get<FetchHSAInvestmentsResponse>(
    `/cdh/v2/accounts/${account_id}/hsa-investments`
  );
}

export interface FetchPretaxPlanResponse {
  pretax_plan: {
    alegeus_plan_id: string;
    alegeus_record_id: string;
    company_id: string;
    grace_period_date: string;
    grace_period_date_formatted: string;
    plan_end_date: string;
    plan_end_date_formatted: string;
    plan_start_date: string;
    plan_start_date_formatted: string;
    runout_period_date: string;
    runout_period_date_formatted: string;
    twic_plan_type: string;
  };
}

export function fetchPretaxPlan({ plan_type }: { plan_type: string }) {
  return gatewayClient.get(`/cdh/v2/accounts/pretax-plans/${plan_type}`);
}
